import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  flip,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import styled, { css, keyframes } from 'styled-components';

import useGadgetDrillTarget from 'components/InsightsChat/explainValue/useGadgetDrillTarget';
import DashboardContextMenuContext from 'contexts/DashboardContextMenuContext';
import Button from 'kingpin/atoms/Button';
import Typography from 'kingpin/atoms/Typography';

// A bare 'constants' import resolves to Node's built-in module
import { Z_INDEX } from '../../constants';
import { CardActions } from './CardChrome';
import { NO_DRAG_CLASS } from './constants';
import StudioContentTypeContext from './StudioContentTypeContext';

// A chart's drill-down while it's being edited in Studio: the popup it
// opens, and how to set it up
export interface StudioChartDrillDown {
  reportDrillDownId?: string;
  boardDrillDownId?: string;
  // Opens the chart's Drill-down settings, with the popup picker open
  onEdit: () => void;
}

// A chart block's (or Composite chart element's) drill-down menu while it's
// edited on a dashboard; none in fullscreen or on the view (no actions), nor
// on a report, which has no popups
export const useStudioChartDrillDown = ({
  content,
  actions,
  elementId,
}: {
  content: Pick<Studio.Card, 'reportDrillDownId' | 'boardDrillDownId'>;
  actions: CardActions | undefined;
  elementId?: string;
}): StudioChartDrillDown | undefined => {
  const contentType = useContext(StudioContentTypeContext);
  const { reportDrillDownId, boardDrillDownId } = content;
  const onOpenSettings = actions ? actions.onOpenSettings : undefined;
  return useMemo(
    () =>
      onOpenSettings && contentType === 'dashboard'
        ? {
            reportDrillDownId,
            boardDrillDownId,
            onEdit: () => onOpenSettings('drillDown', elementId),
          }
        : undefined,
    [
      boardDrillDownId,
      contentType,
      elementId,
      onOpenSettings,
      reportDrillDownId,
    ],
  );
};

const popIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-4px) scale(0.96);
  }
`;

// A head shake: clicking through goes nowhere yet
const shake = keyframes`
  0%, 100% { transform: none; }
  20% { transform: translateX(-6px); }
  40% { transform: translateX(5px); }
  60% { transform: translateX(-4px); }
  80% { transform: translateX(2px); }
`;

// Pops in by the click. Without a drill-down it shakes as it lands, as
// clicking through opens nothing.
const List = styled.div<{ $isShaking: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 200px;
  padding: 8px;
  border: 1px solid var(--color-border-primary);
  border-radius: 10px;
  background-color: var(--color-bg-primary);
  box-shadow: var(--shadow-md);
  transform-origin: top left;
  animation: ${popIn} 160ms ease-out both;

  ${(props) =>
    props.$isShaking &&
    css`
      animation:
        ${popIn} 160ms ease-out both,
        ${shake} 360ms 120ms ease-in-out;
    `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

// Fixed by the click, so neither the chart nor the canvas clips it. The
// block still hears its presses through the portal, so it's no drag handle.
const Floating = styled.div`
  z-index: ${Z_INDEX.INLINE_DIALOG};
`;

// Lets the click through to the chart, noting where it landed
const ClickTracker = styled.div`
  display: contents;
`;

// The popup it opens
const Status = styled.div`
  padding: 2px 4px;
`;

// In place of a popup, an empty slot for one
const Placeholder = styled.div`
  padding: 8px 10px;
  border: 1px dashed var(--color-border-primary);
  border-radius: 6px;
  background-color: var(--color-bg-secondary);
  text-align: center;
`;

// The chart click menu in Studio, on a dashboard: rather than the
// dashboard's (metric records, Explain), what clicking through would open
// and a way to set it, since a chart without a popup opens nothing
const StudioChartMenu = ({
  drillDown,
  children,
}: {
  drillDown: StudioChartDrillDown;
  children: JSX.Element;
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  // Each click draws the menu afresh, so it pops in (and shakes) again
  const [openCount, setOpenCount] = useState<number>(0);
  // Where the click that opens it landed: the chart opens it from its own
  // click handler, which knows the point but not the pointer
  const clickPosition = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const target = useGadgetDrillTarget(drillDown);
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'bottom-start',
    strategy: 'fixed',
    middleware: [offset(6), flip({ padding: 8 }), shift({ padding: 8 })],
  });
  // Closed by a click elsewhere, Escape, or the canvas scrolling away from
  // where it was opened
  const dismiss = useDismiss(context, { ancestorScroll: true });
  const { getFloatingProps } = useInteractions([dismiss]);
  const onClickCapture = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    clickPosition.current = { x: e.clientX, y: e.clientY };
  }, []);
  const { setPositionReference } = refs;
  const openMenu = useCallback(() => {
    const { x, y } = clickPosition.current;
    setPositionReference({
      getBoundingClientRect: () => ({
        x,
        y,
        top: y,
        left: x,
        bottom: y,
        right: x,
        width: 0,
        height: 0,
      }),
    });
    setOpenCount((count) => count + 1);
    setIsOpen(true);
  }, [setPositionReference]);
  const closeMenu = useCallback(() => setIsOpen(false), []);
  const contextValue = useMemo(
    () => ({
      isOpen,
      openMenu,
      closeMenu,
      metricIds: [],
      setSelectedMetric: () => {},
    }),
    [closeMenu, isOpen, openMenu],
  );
  const { onEdit } = drillDown;

  return (
    <DashboardContextMenuContext.Provider value={contextValue}>
      <React.Fragment>
        <ClickTracker onClickCapture={onClickCapture}>{children}</ClickTracker>
        {isOpen && (
          <FloatingPortal>
            <Floating
              ref={refs.setFloating}
              style={floatingStyles}
              className={NO_DRAG_CLASS}
              {...getFloatingProps({
                // Its clicks are its own, not the block's it's portalled from
                onClick: (e: React.MouseEvent) => e.stopPropagation(),
              })}
            >
              <List
                key={openCount}
                $isShaking={!target}
                data-testid="studio-chart-menu"
              >
                {target ? (
                  <Status>
                    <Typography.Body
                      type="Body 12"
                      color="var(--color-text-secondary)"
                    >
                      {`Drill-down: ${target.name ?? `a ${target.type}`}`}
                    </Typography.Body>
                  </Status>
                ) : (
                  <Placeholder>
                    <Typography.Body
                      type="Body 12"
                      color="var(--color-text-tertiary)"
                    >
                      No drill-down
                    </Typography.Body>
                  </Placeholder>
                )}
                <Button
                  type={target ? 'Secondary' : 'Primary'}
                  size="Small"
                  label={target ? 'Change popup report' : 'Add popup report'}
                  onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                    e.preventDefault();
                    e.stopPropagation();
                    closeMenu();
                    onEdit();
                  }}
                  testId="studio-chart-menu-edit-popup"
                />
              </List>
            </Floating>
          </FloatingPortal>
        )}
      </React.Fragment>
    </DashboardContextMenuContext.Provider>
  );
};

export default StudioChartMenu;
